
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";

import {
  HiOutlineArrowUpRight,
  HiOutlineBookOpen,
  HiOutlineCalendarDays,
  HiOutlineEye,
  HiOutlineHandThumbUp,
  HiHandThumbUp,
  HiOutlineScale,
  HiOutlineChevronRight,
} from "react-icons/hi2";

import Header from "../components/layout/Header";
import Fotter from "../components/layout/Fotter";

import RelatedBlogs from "../components/RelatedBlogs";
import RelatedBigBlogs from "../components/RelatedBigBlogs";

import ShareArticle from "../components/utils/ShareArticle";
import { Loader } from "../components/utils";

import { getBlog } from "../redux/actions/blogs";
import { BaseUrl, BlogUrl, media } from "../server";

/* ============================================
   HELPERS
============================================ */

function getImageUrl(image) {
  if (!image || typeof image !== "string") return null;

  const filename = image.trim();
  if (!filename) return null;

  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  const base = String(media || "").replace(/\/+$/, "");
  const path = filename.replace(/^\/+/, "");

  return base ? `${base}/${path}` : `/${path}`;
}

function getPlainText(text) {
  if (typeof text !== "string") return "";

  const html = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  const element = document.createElement("div");
  element.innerHTML = html;

  return (element.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   ARTICLE CONTENT
============================================ */

function ArticleContent({ value }) {
  if (!value || typeof value !== "string") return null;

  const hasHtml = /<\/?[a-z][^>]*>/i.test(value);

  const contentClasses = `
    font-kufi
    text-[15px]
    leading-[2.15]
    text-skin-text
    sm:text-base
    lg:text-[17px]

    break-words

    [&_p]:mb-6
    [&_p]:leading-[2.15]

    [&_h2]:mb-5
    [&_h2]:mt-10
    [&_h2]:text-2xl
    [&_h2]:font-bold
    [&_h2]:text-skin-heading

    [&_h3]:mb-4
    [&_h3]:mt-8
    [&_h3]:text-xl
    [&_h3]:font-semibold
    [&_h3]:text-skin-heading

    [&_strong]:font-bold
    [&_strong]:text-skin-heading

    [&_ul]:mb-6
    [&_ul]:list-disc
    [&_ul]:ps-7

    [&_ol]:mb-6
    [&_ol]:list-decimal
    [&_ol]:ps-7

    [&_li]:mb-2
    [&_li]:ps-1

    [&_a]:text-skin-accent
    [&_a]:underline

    [&_blockquote]:my-8
    [&_blockquote]:border-s-2
    [&_blockquote]:border-skin-accent
    [&_blockquote]:bg-skin-soft
    [&_blockquote]:px-6
    [&_blockquote]:py-4

    [&_img]:my-7
    [&_img]:max-w-full
    [&_img]:rounded-xl

    [&_table]:block
    [&_table]:max-w-full
    [&_table]:overflow-x-auto
  `;

  if (hasHtml) {
    const safeHtml = DOMPurify.sanitize(value, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "style", "iframe"],
      FORBID_ATTR: ["style"],
    });

    return (
      <div
        className={contentClasses}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  }

  return (
    <div className={contentClasses}>
      {value
        .split(/\n\s*\n/)
        .filter(Boolean)
        .map((paragraph, index) => (
          <p key={index} className="whitespace-pre-line">
            {paragraph.trim()}
          </p>
        ))}
    </div>
  );
}

/* ============================================
   BLOG DETAILS PAGE
============================================ */

function BlogeDetails() {
  const { id } = useParams();

  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const { BlogData, isLoadingBLog } = useSelector(
    (state) => state.Blogs || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const [liked, setLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [imageError, setImageError] = useState(false);

  /* ======================================
     GET ARTICLE
  ====================================== */

  useEffect(() => {
    if (id) {
      dispatch(getBlog(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    setLiked(false);
    setIsLiking(false);
    setImageError(false);
  }, [id]);

  const blog =
    BlogData &&
    String(BlogData._id) === String(id)
      ? BlogData
      : null;

  const title = isEnglish
    ? blog?.titleEn
    : blog?.titleAr;

  const articleText = isEnglish
    ? blog?.textEn
    : blog?.textAr;

  const imageUrl = getImageUrl(blog?.Image);

  const views = Number(blog?.views ?? 0);
  const likes = Number(blog?.likes ?? 0) + (liked ? 1 : 0);

  const shareUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `${BlogUrl}${id}`;

  const labels = {
    home: isEnglish ? "Home" : "الرئيسية",
    blogs: isEnglish ? "Articles" : "المقالات",
    category: isEnglish
      ? "Legal Knowledge"
      : "المعرفة القانونية",
    views: isEnglish ? "Views" : "المشاهدات",
    likes: isEnglish ? "Likes" : "الإعجابات",
    like: isEnglish ? "Like Article" : "أعجبني المقال",
    liked: isEnglish ? "Liked" : "تم الإعجاب",
    share: isEnglish ? "Share Article" : "شارك هذا المقال",
    notFound: isEnglish
      ? "This article is currently unavailable."
      : "هذا المقال غير متاح حاليًا.",
    browse: isEnglish
      ? "Browse Articles"
      : "تصفح المقالات",
    error: isEnglish
      ? "Unable to save your like. Please try again."
      : "تعذر تسجيل الإعجاب. يرجى المحاولة مجددًا.",
  };

  /* ======================================
     ARTICLE LIKE
  ====================================== */

  const handleLike = async () => {
    if (!blog || liked || isLiking) return;

    setIsLiking(true);

    try {
      // Keep the existing backend endpoint
      const { data } = await axios.get(
        `${BaseUrl}/blogs/like/${id}`
      );

      if (data?.success) {
        setLiked(true);
      } else {
        toast.error(labels.error);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || labels.error
      );
    } finally {
      setIsLiking(false);
    }
  };

  /* ======================================
     RENDER
  ====================================== */

  return (
    <>
      <Header />

      <main
        className="
          min-h-screen
          bg-skin-page
          text-skin-text
          transition-colors duration-300
        "
      >
        {isLoadingBLog ? (
          <div className="flex min-h-[65vh] items-center justify-center">
            <Loader />
          </div>
        ) : !blog ? (
          <div className="container py-28 text-center">
            <HiOutlineBookOpen
              size={46}
              className="mx-auto mb-6 text-skin-accent"
            />

            <h1 className="h4 text-skin-heading">
              {labels.notFound}
            </h1>

            <Link
              to="/BlogsCategory"
              className="button-sm mt-7 inline-flex items-center gap-2 text-skin-accent"
            >
              {labels.browse}
              <HiOutlineArrowUpRight
                className="directional-arrow"
                size={19}
              />
            </Link>
          </div>
        ) : (
          <>
            {/* ==================================
                ARTICLE HEADER
            ================================== */}

            <section className="border-b border-skin-border bg-skin-surface">
              <div className="container py-10 md:py-14 lg:py-16">

                {/* Breadcrumb */}
                <nav
                  aria-label="Breadcrumb"
                  className="mb-8 flex flex-wrap items-center gap-2 text-skin-muted"
                >
                  <Link
                    to="/"
                    className="body-3 transition-colors hover:text-skin-accent"
                  >
                    {labels.home}
                  </Link>

                  <HiOutlineChevronRight
                    size={15}
                    aria-hidden="true"
                    className="directional-arrow"
                  />

                  <Link
                    to="/BlogsCategory"
                    className="body-3 transition-colors hover:text-skin-accent"
                  >
                    {labels.blogs}
                  </Link>
                </nav>

                {/* Article category label */}
                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-10 bg-skin-accent" />

                  <span className="eyebrow text-skin-accent">
                    {labels.category}
                  </span>
                </div>

                {/* Main article title */}
                <h1
                  className="
                    h1 max-w-[1050px]
                    text-skin-heading
                  "
                >
                  {title}
                </h1>

                {/* Article metadata */}
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">

                  {blog.date && (
                    <div className="flex items-center gap-2 text-skin-muted">
                      <HiOutlineCalendarDays size={19} />
                      <span className="body-3">
                        {blog.date}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-skin-muted">
                    <HiOutlineEye size={19} />

                    <span className="body-3">
                      {views.toLocaleString("en-US")} {labels.views}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-skin-muted">
                    <HiOutlineHandThumbUp size={19} />

                    <span className="body-3">
                      {likes.toLocaleString("en-US")} {labels.likes}
                    </span>
                  </div>

                </div>
              </div>
            </section>

            {/* ==================================
                MAIN ARTICLE & SIDEBAR
            ================================== */}

            <section className="py-8 md:py-12 lg:py-14">
              <div className="container">

                {/* Main content first:
                    RTL -> article right, sidebar left
                    LTR -> article left, sidebar right */}

                <div
                  className="
                    grid grid-cols-1
                    items-start gap-8

                    lg:grid-cols-[minmax(0,1fr)_330px]
                    lg:gap-9
                  "
                >
                  {/* ==========================
                      ARTICLE
                  ========================== */}

                  <article
                    className="
                      min-w-0 overflow-hidden
                      rounded-[24px]

                      border border-skin-border
                      bg-skin-surface

                      shadow-[0_12px_40px_rgba(15,39,55,0.045)]

                      dark:shadow-none
                    "
                  >
                    {/* Article image */}
                    <figure
                      className="
                        relative aspect-[16/9]
                        overflow-hidden
                        bg-[#162D40]
                        sm:aspect-[16/8]
                      "
                    >
                      {imageUrl && !imageError ? (
                        <img
                          src={imageUrl}
                          alt={title || ""}
                          onError={() => setImageError(true)}
                          className="
                            h-full w-full
                            object-cover object-center
                          "
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#294961] to-[#0B1726]">
                          <HiOutlineScale
                            size={75}
                            className="text-[#D7B77F]/65"
                          />
                        </div>
                      )}
                    </figure>

                    {/* Article body */}
                    <div
                      className="
                        px-5 py-8

                        sm:px-8 sm:py-10
                        lg:px-11 lg:py-12
                      "
                    >
                      <div className="mb-9 flex items-center gap-3">
                        <span className="h-1.5 w-1.5 rounded-full bg-skin-accent" />
                        <span className="h-px w-12 bg-skin-accent/60" />
                      </div>

                      <ArticleContent value={articleText} />

                      {/* ========================
                          ARTICLE ACTIONS
                      ======================== */}

                      <div className="mt-12 border-t border-skin-border pt-7">

                        <div
                          className="
                            flex flex-col gap-7
                            sm:flex-row
                            sm:items-center
                            sm:justify-between
                          "
                        >
                          {/* Like button */}
                          <button
                            type="button"
                            onClick={handleLike}
                            disabled={liked || isLiking}
                            aria-pressed={liked}
                            className={`
                              group inline-flex min-h-[46px]
                              items-center justify-center
                              gap-3 self-start

                              rounded-xl
                              border px-5 py-2.5

                              font-kufi text-sm
                              font-semibold

                              transition-all duration-300

                              focus-visible:outline-none
                              focus-visible:ring-2
                              focus-visible:ring-skin-accent

                              ${
                                liked
                                  ? "border-skin-accent/35 bg-skin-accent/10 text-skin-accent"
                                  : "border-skin-border bg-skin-soft text-skin-heading hover:border-skin-accent/35 hover:bg-skin-raised"
                              }

                              disabled:cursor-default
                            `}
                          >
                            {liked ? (
                              <HiHandThumbUp size={21} />
                            ) : (
                              <HiOutlineHandThumbUp size={21} />
                            )}

                            <span>
                              {liked ? labels.liked : labels.like}
                            </span>

                            <span className="tabular-nums">
                              {likes}
                            </span>
                          </button>

                          {/* Views */}
                          <div className="flex items-center gap-2 text-skin-muted">
                            <HiOutlineEye size={19} />
                            <span className="body-3">
                              {views.toLocaleString("en-US")} {labels.views}
                            </span>
                          </div>
                        </div>

                        {/* Share area */}
                        <div
                          className="
                            mt-8
                            rounded-2xl
                            border border-skin-border
                            bg-skin-soft
                            px-5 py-5
                            sm:px-6
                          "
                        >
                          <h2 className="h7 mb-4 text-skin-heading">
                            {labels.share}
                          </h2>

                          <ShareArticle
                            url={shareUrl}
                            title={title}
                            description={getPlainText(articleText).slice(0, 180)}
                          />
                        </div>

                      </div>
                    </div>

                  </article>

                  {/* ==========================
                      RELATED SERVICES
                  ========================== */}

                  <aside className="min-w-0 lg:sticky lg:top-[105px]">
                    <RelatedBlogs />
                  </aside>

                </div>
              </div>
            </section>

            {/* ==================================
                RELATED ARTICLES
            ================================== */}

            <RelatedBigBlogs currentBlogId={id} />
          </>
        )}
      </main>

      <Fotter />
    </>
  );
}

export default BlogeDetails;
